import React, { useCallback, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { Field, FieldProps, Form as FormikForm, Formik } from 'formik'

import { Form, Disease, PreconditionsFormProps, PreconditionsProps, LayoutItem } from '../../../ana-toolbox'
import { DiseaseType } from '../../../ana-toolbox'
import { DiseaseGroupField } from '../../components/disease-group-field/disease-group-field'
import { FormLayout } from '../../components/form-layout/form-layout'
import { SubmitButton } from '../../components/submit-button/submit-button'
import { Translate } from '../../components/translate/translate'
import { diseasesByType, diseasesForPage1, diseasesForPage2, diseasesForPage3 } from '../../utils/config/diseases'
import { usePreConditionsLogic } from '../../utils/hooks/usePreConditionsToggle'

import * as S from './preconditions.styles'

const groupDiseaseByType = (diseases: Disease[]): Record<string, Disease[]> =>
  diseases.reduce<Record<string, Disease[]>>((grouped, disease) => {
    const previousDiseasesForCurrentType = grouped[disease.type] || []

    return {
      ...grouped,
      [disease.type]: [...previousDiseasesForCurrentType, disease],
    }
  }, {})

const helperTexts: Record<string, { helperText: string; activeHelperText: string; placeholder: string }> = {
  [DiseaseType.SURGERY]: {
    helperText: 'which_surgeries_subtitle',
    activeHelperText: 'field.which_surgeries_subtitle',
    placeholder: 'field.did_you_ever_have_surgery_placeholder',
  },
  [DiseaseType.OTHER]: {
    helperText: 'which_other_disease_subtitle',
    activeHelperText: 'field.which_other_disease_subtitle',
    placeholder: 'field.other_disease_placeholder',
  },
}

export const PreConditionsForm = ({
  initialValues = {},
  diseases,
  diseaseGroupLabelByIndex = {},
  showSurgery,
  onSubmit,
  loading,
  innerRef,
  portalAttr,
  logic,
}: PreconditionsProps) => {
  const preconditionsLogic = usePreConditionsLogic({
    initialValues,
    diseases,
  })

  const {
    errors,
    hasError,
    collapseStates,
    selectedDiseases,
    handleToggleChange,
    handleDiseaseCheck,
    handleDiseaseChange,
    ...preConditionsLogic
  } = logic || preconditionsLogic

  const handleSubmit = (values: Partial<Form>) => {
    preConditionsLogic.handleSubmit()

    onSubmit({
      ...values,
      diseases: selectedDiseases,
    })
  }

  const { t } = useTranslation()

  const diseasesGroupedEntries = useMemo(() => {
    const diseasesGroupedByType = groupDiseaseByType(diseases)

    return Object.entries(diseasesGroupedByType)
  }, [diseases])

  const renderDiseaseGroup = useCallback(
    ([groupType, groupDiseases]: [DiseaseType, Disease[]], index: number) => {
      const diseaseType = groupType as DiseaseType
      const label = diseaseGroupLabelByIndex[index]
      const originalError = errors[diseaseType]
      const isSurgeryMissing = groupType === DiseaseType.SURGERY && originalError === 'disease_required'
      const error = isSurgeryMissing ? 'surgery_required' : originalError

      return (
        <DiseaseGroupField
          key={diseaseType}
          type={diseaseType}
          label={label && `disease_prefix_${label}`}
          diseases={diseasesByType[diseaseType]}
          selectedDiseases={selectedDiseases}
          allDiseases={groupDiseases}
          error={error}
          checked={Boolean(collapseStates[diseaseType])}
          onToggle={handleToggleChange}
          onDiseaseCheck={handleDiseaseCheck}
          onDiseaseChange={handleDiseaseChange}
          hideMedications={diseaseType === DiseaseType.SURGERY}
          activeHelperText={helperTexts[diseaseType]?.activeHelperText}
          customInputPlaceholder={helperTexts[diseaseType]?.placeholder}
        />
      )
    },
    [
      diseaseGroupLabelByIndex,
      collapseStates,
      handleDiseaseChange,
      handleDiseaseCheck,
      handleToggleChange,
      errors,
      selectedDiseases,
    ]
  )

  const layout: LayoutItem[] = useMemo(() => {
    const diseaseItems: LayoutItem[] = diseasesGroupedEntries.map(([groupType, groupDiseases], index) => ({
      type: 'custom',
      key: groupType,
      xs: 12,
      render: () => renderDiseaseGroup([groupType as DiseaseType, groupDiseases], showSurgery ? index + 1 : index),
    }))

    const surgeryItems: LayoutItem[] = showSurgery
      ? [
          {
            type: 'custom',
            key: DiseaseType.SURGERY,
            xs: 12,
            render: () => renderDiseaseGroup([DiseaseType.SURGERY, diseasesByType[DiseaseType.SURGERY]], 0),
          },
        ]
      : []

    return [
      {
        type: 'custom',
        key: 'helper',
        xs: 12,
        align: 'center',
        render: () => (
          <S.HelperText>
            <Translate>pre_existing_conditions_helper_text</Translate>
          </S.HelperText>
        ),
      },
      ...surgeryItems,
      ...diseaseItems,
    ]
  }, [showSurgery, renderDiseaseGroup, diseasesGroupedEntries])

  const submitButton = (
    <Field>
      {(props: FieldProps) => {
        return (
          <SubmitButton
            loading={loading}
            onClick={() => props.form.submitForm()}
            disabled={!props.form.isValid || hasError}
          >
            {t('next')}
          </SubmitButton>
        )
      }}
    </Field>
  )

  return (
    <Formik innerRef={innerRef} initialValues={initialValues} onSubmit={handleSubmit}>
      <FormikForm>
        <FormLayout portalAttr={portalAttr} items={layout} submitButton={submitButton} />
      </FormikForm>
    </Formik>
  )
}

export const PreConditionsForm1 = (props: PreconditionsFormProps) => (
  <PreConditionsForm
    {...props}
    diseaseGroupLabelByIndex={{
      0: 'had',
      1: 'have',
    }}
    showSurgery
    diseases={diseasesForPage1}
  />
)

export const PreConditionsForm2 = (props: PreconditionsFormProps) => (
  <PreConditionsForm
    {...props}
    diseaseGroupLabelByIndex={{
      0: 'have',
    }}
    diseases={diseasesForPage2}
  />
)

export const PreConditionsForm3 = (props: PreconditionsFormProps) => (
  <PreConditionsForm {...props} diseaseGroupLabelByIndex={{ 0: 'have' }} diseases={diseasesForPage3} />
)
